import { Immutable } from "immer";
import React from "react";
import { ValuePropsBarBlock } from "tsi-common-react/src/components/ValuePropsBarBlock/index";

import { templates } from "@reactivated";
import type { ValuePropBarBlock as ValuePropBarBlockValue } from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

/**
 * toppers LandingPage ``value_props`` assembly: collapses the per-block
 * ``{% for block in self.value_props %}{% if block.block_type == 'value_prop_bar'
 * %}{% include_block block %}{% endif %}{% endfor %}`` loop in
 * ``cms/landing_page.html`` into a SINGLE ``{% include_react %}`` island.
 *
 * NO WRAPPER (Fragment): the loop emitted each block via a bare
 * ``{% include_block block %}``, so
 * Wagtail's ``<div class="w-block-...">`` wrapper was never applied. The
 * ``StreamField`` with ``tagName={React.Fragment}`` renders each
 * ``.value-prop-bar-block`` slide bare -- matching the output exactly.
 *
 * SINGLE-TYPE FILTER: ``value_props`` is a single-type StreamField (only
 * ``value_prop_bar``), so every block maps and the ``block_type ==
 * 'value_prop_bar'`` ``{% if %}`` is a no-op today. NOTE: thelabui
 * ``StreamField`` THROWS on an unmapped block type (it does NOT silently drop
 * like the ``{% if %}``), so if a SECOND type were ever added to this
 * field, a BodyBottom-style pre-filter would be needed. Not added here -- the
 * field cannot hold another type today.
 *
 * NO GLOBAL JS: the ``value_prop_bar`` bar is a static ``<ul>`` whose only
 * interactivity is the self-contained per-item ``UncontrolledPopover`` tooltip
 * (pure React). The ``landing-page-value-props.ts`` cycler drives the SEPARATE
 * ``product_value_prop_block`` (inside ``landing_page_props``), NOT this field
 * -- so islandizing here has no hydration-survival concern and needs no
 * initial-state baking.
 *
 * The ``value_prop_bar`` adapter: the StreamField
 * child injects ``{ value }`` while ``ValuePropsBarBlock`` takes
 * ``valuePropsData``.
 */
const valuePropsComponentsMap = {
    value_prop_bar: (block: { value: Immutable<ValuePropBarBlockValue> }) => (
        <ValuePropsBarBlock valuePropsData={block.value} />
    ),
} as const;

export const LandingPageValueProps = (
    props: Immutable<{
        page: Pick<templates.LandingPageTemplate["page"], "value_props">;
    }>,
): React.JSX.Element | null => {
    const valueProps = props.page.value_props;
    if (!valueProps) {
        return null;
    }
    return (
        <StreamField
            tagName={React.Fragment}
            value={valueProps.value}
            components={valuePropsComponentsMap}
        />
    );
};
